import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { CAT_MAP } from "@/lib/categories";
import { TrendingUp, TrendingDown, Minus, ArrowUpRight } from "lucide-react";

const TrendAreaChart = lazy(() => import("./TrendAreaChart"));

function delta(history) {
  if (!history || history.length < 2) return null;
  const first = Number(history[0].value);
  const last = Number(history[history.length - 1].value);
  if (!first) return null;
  return ((last - first) / first) * 100;
}

export function TrendChart({ history, hex, id, height = 120 }) {
  const gid = `grad-${id}`;
  if (!history || history.length < 2) {
    return (
      <div style={{ height }} data-testid="trend-building"
        className="flex flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-line bg-ink text-center px-4">
        <span className="font-meta text-[10px] uppercase tracking-[0.18em] text-stone-500">Storico in costruzione</span>
        <span className="text-[11px] text-stone-500 leading-snug">
          Registriamo un punto reale a ogni revisione della stima: il grafico parte dal secondo rilevamento.
        </span>
      </div>
    );
  }
  return (
    <Suspense fallback={<div style={{ height }} className="rounded-xl bg-ink border-2 border-line" />}>
      <TrendAreaChart history={history} hex={hex} id={id} height={height} />
    </Suspense>
  );
}

export function TrendDelta({ history }) {
  const d = delta(history);
  if (d === null) return null;
  const up = d > 0.5, down = d < -0.5;
  const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
  const color = up ? "text-emerald-600" : down ? "text-rose-600" : "text-stone-600";
  return (
    <span className={`inline-flex items-center gap-1 font-meta text-xs font-semibold ${color}`}>
      <Icon size={13} />{d > 0 ? "+" : ""}{d.toFixed(1)}%
    </span>
  );
}

/** Compact value-card widget embedded inside an article. */
export function ArticleValueTrend({ card, match }) {
  const cat = CAT_MAP[card.category];
  const hex = cat?.hex || "#E5C158";
  const hasHistory = card.history?.length >= 2;

  return (
    <aside data-testid="article-value-trend"
      className="my-10 rounded-2xl bg-panel border border-gold/25 overflow-hidden lift">
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <span className="font-meta text-[10px] uppercase tracking-[0.2em] text-gold">
              {match === "oggetto" ? "Scheda valore collegata" : "Andamento della categoria"}
            </span>
            <h3 className="font-editorial font-bold text-lg text-stone-900 mt-1.5">{card.name}</h3>
          </div>
          {hasHistory && <TrendDelta history={card.history} />}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl bg-ink border-2 border-line px-4 py-3">
          <div>
            <p className="font-meta text-[9px] uppercase tracking-[0.18em] text-stone-500">
              {card.value_source === "ebay-live"
                ? `Prezzi eBay di oggi · ${card.live?.count || 0} inserzioni`
                : "Stima AI indicativa"}
            </p>
            <p data-testid="article-value-range" className="font-meta text-base font-bold text-gold mt-0.5">{card.value_range}</p>
          </div>
          {card.era && (
            <div>
              <p className="font-meta text-[9px] uppercase tracking-[0.18em] text-stone-500">Periodo</p>
              <p className="font-meta text-sm text-stone-700 mt-0.5">{card.era}</p>
            </div>
          )}
        </div>

        <div className="mt-4" data-testid="article-value-chart">
          <p className="font-meta text-[9px] uppercase tracking-[0.18em] text-stone-500 mb-1">
            {card.value_source === "ebay-live" ? "Andamento dei prezzi (€)" : "Andamento della nostra stima (€)"}
          </p>
          <TrendChart history={card.history} hex={hex} id={`art-${card.id}`} height={120} />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 px-5 py-3 bg-ink-deep border-t border-line">
        <p className="text-[10px] text-stone-400 leading-snug max-w-sm">
          {card.value_source === "ebay-live"
            ? <>Mediana dei prezzi richiesti dalle inserzioni in vendita ora su eBay, aggiornata ogni ora:
              <strong> non</strong> sono aggiudicazioni, né una perizia o una consulenza d'investimento.</>
            : <>Stima generata dalla nostra AI e rivista ogni ora: <strong>non</strong> sono quotazioni di mercato,
              né una perizia o una consulenza d'investimento.</>}
        </p>
        <Link to="/valori" data-testid="article-value-link"
          className="shrink-0 inline-flex items-center gap-1 font-meta text-[11px] font-semibold text-gold hover:text-gold-soft transition-colors">
          Schede Valore <ArrowUpRight size={13} />
        </Link>
      </div>
    </aside>
  );
}
